import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft,
  Ear,
  Save,
  Trash2,
  RotateCcw,
  Info,
  Sparkles,
  ShoppingCart,
  Star
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
  ScatterChart,
  ZAxis
} from "recharts";

// Standard audiogram frequencies
const FREQUENCIES = [125, 250, 500, 1000, 2000, 4000, 8000];
const DB_LEVELS = [-10, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120];

export default function PTATestPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(patientId || "");
  const [selectedClinic, setSelectedClinic] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [savedTestId, setSavedTestId] = useState(null);
  
  // Audiogram data state
  const [audiogramData, setAudiogramData] = useState([]);
  const [currentEar, setCurrentEar] = useState("right");
  const [currentConduction, setCurrentConduction] = useState("air");
  const [isMasked, setIsMasked] = useState(false);

  useEffect(() => {
    fetchPatients();
    fetchClinics();
  }, []);

  useEffect(() => {
    if (patientId) {
      setSelectedPatient(patientId);
    }
  }, [patientId]);

  const fetchPatients = async () => {
    try {
      const response = await apiClient.get("/patients");
      setPatients(response.data);
    } catch (error) {
      console.error("Failed to fetch patients:", error);
    }
  };

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get("/clinics");
      setClinics(response.data);
      if (response.data.length > 0) {
        setSelectedClinic(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch clinics:", error);
    }
  };

  const handleCanvasClick = (e) => {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Calculate frequency and dB from click position
    const chartWidth = rect.width - 80; // Accounting for margins
    const chartHeight = rect.height - 60;
    
    const xOffset = 50;
    const yOffset = 20;
    
    const relativeX = (x - xOffset) / chartWidth;
    const relativeY = (y - yOffset) / chartHeight;
    
    // Map to frequency (log scale approximation)
    const freqIndex = Math.round(relativeX * (FREQUENCIES.length - 1));
    const frequency = FREQUENCIES[Math.max(0, Math.min(freqIndex, FREQUENCIES.length - 1))];
    
    // Map to dB (-10 to 120)
    const db = Math.round(relativeY * 130 - 10);
    const roundedDb = Math.round(db / 5) * 5; // Round to nearest 5 dB
    const clampedDb = Math.max(-10, Math.min(120, roundedDb));
    
    // Add or update data point
    const newPoint = {
      frequency,
      threshold_db: clampedDb,
      ear: currentEar,
      conduction_type: currentConduction,
      masked: isMasked
    };
    
    // Remove existing point for this frequency/ear/conduction combination
    const filtered = audiogramData.filter(d => 
      !(d.frequency === frequency && d.ear === currentEar && d.conduction_type === currentConduction)
    );
    
    setAudiogramData([...filtered, newPoint]);
    toast.success(`Added: ${frequency} Hz at ${clampedDb} dB (${currentEar} ear, ${currentConduction})`);
  };

  const removeLastPoint = () => {
    if (audiogramData.length > 0) {
      const lastEarData = audiogramData.filter(d => d.ear === currentEar && d.conduction_type === currentConduction);
      if (lastEarData.length > 0) {
        const lastPoint = lastEarData[lastEarData.length - 1];
        setAudiogramData(audiogramData.filter(d => 
          !(d.frequency === lastPoint.frequency && d.ear === currentEar && d.conduction_type === currentConduction)
        ));
        toast.info("Removed last point");
      }
    }
  };

  const clearAudiogram = () => {
    setAudiogramData([]);
    toast.info("Audiogram cleared");
  };

  const handleSaveTest = async () => {
    if (!selectedPatient) {
      toast.error("Please select a patient");
      return;
    }
    if (audiogramData.length === 0) {
      toast.error("Please record audiogram data");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        patient_id: selectedPatient,
        clinic_id: selectedClinic || clinics[0]?.id,
        audiogram_data: audiogramData,
        notes: notes || null
      };
      
      const response = await apiClient.post("/pta-tests", payload);
      toast.success("PTA Test saved successfully!");
      setSavedTestId(response.data.id);
      
      // Get AI recommendations immediately
      setLoadingRecommendations(true);
      setShowRecommendations(true);
      
      try {
        const recResponse = await apiClient.post(`/pta-tests/${response.data.id}/quick-recommendation`);
        setAiRecommendations(recResponse.data);
      } catch (recError) {
        console.error("Failed to get recommendations:", recError);
        // Still show dialog but without recommendations
        setAiRecommendations({
          severity_assessment: {
            right: { pta: response.data.pta_right, severity: response.data.severity_right },
            left: { pta: response.data.pta_left, severity: response.data.severity_left },
            overall: response.data.severity_right
          },
          quick_recommendations: [],
          counseling_note: "Please generate recommendations from the Recommendations page.",
          next_steps: "Proceed to AI Recommendations for detailed analysis"
        });
      } finally {
        setLoadingRecommendations(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save test");
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = () => {
    setShowRecommendations(false);
    navigate(`/patients/${selectedPatient}`);
  };

  const handleGoToRecommendations = () => {
    setShowRecommendations(false);
    navigate(`/recommendations?patient=${selectedPatient}`);
  };

  // Prepare chart data
  const rightAirData = audiogramData.filter(d => d.ear === "right" && d.conduction_type === "air");
  const leftAirData = audiogramData.filter(d => d.ear === "left" && d.conduction_type === "air");
  const rightBoneData = audiogramData.filter(d => d.ear === "right" && d.conduction_type === "bone");
  const leftBoneData = audiogramData.filter(d => d.ear === "left" && d.conduction_type === "bone");

  // Combine data for recharts
  const chartData = FREQUENCIES.map(freq => {
    const rightAir = rightAirData.find(d => d.frequency === freq);
    const leftAir = leftAirData.find(d => d.frequency === freq);
    const rightBone = rightBoneData.find(d => d.frequency === freq);
    const leftBone = leftBoneData.find(d => d.frequency === freq);
    
    return {
      frequency: freq,
      rightAir: rightAir?.threshold_db,
      leftAir: leftAir?.threshold_db,
      rightBone: rightBone?.threshold_db,
      leftBone: leftBone?.threshold_db
    };
  });

  // Custom marker components
  const RightEarMarker = (props) => {
    const { cx, cy } = props;
    if (!cx || !cy) return null;
    return <circle cx={cx} cy={cy} r={6} fill="none" stroke="#EF4444" strokeWidth={2} />;
  };

  const LeftEarMarker = (props) => {
    const { cx, cy } = props;
    if (!cx || !cy) return null;
    return (
      <g>
        <line x1={cx-5} y1={cy-5} x2={cx+5} y2={cy+5} stroke="#3B82F6" strokeWidth={2} />
        <line x1={cx+5} y1={cy-5} x2={cx-5} y2={cy+5} stroke="#3B82F6" strokeWidth={2} />
      </g>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="pta-test-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                Pure Tone Audiometry
              </h1>
              <p className="text-slate-500">Record audiogram data by clicking on the chart</p>
            </div>
          </div>
          <Button 
            className="bg-sky-600 hover:bg-sky-700"
            onClick={handleSaveTest}
            disabled={saving || audiogramData.length === 0}
            data-testid="save-pta-test-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Test
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Controls */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Test Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Patient Selection */}
              <div>
                <Label>Patient *</Label>
                <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                  <SelectTrigger data-testid="patient-select">
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.age} yrs)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ear Selection */}
              <div>
                <Label>Ear</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    variant={currentEar === "right" ? "default" : "outline"}
                    className={currentEar === "right" ? "bg-red-500 hover:bg-red-600" : ""}
                    onClick={() => setCurrentEar("right")}
                    data-testid="right-ear-btn"
                  >
                    Right (O)
                  </Button>
                  <Button
                    variant={currentEar === "left" ? "default" : "outline"}
                    className={currentEar === "left" ? "bg-blue-500 hover:bg-blue-600" : ""}
                    onClick={() => setCurrentEar("left")}
                    data-testid="left-ear-btn"
                  >
                    Left (X)
                  </Button>
                </div>
              </div>

              {/* Conduction Type */}
              <div>
                <Label>Conduction Type</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    variant={currentConduction === "air" ? "default" : "outline"}
                    onClick={() => setCurrentConduction("air")}
                  >
                    Air
                  </Button>
                  <Button
                    variant={currentConduction === "bone" ? "default" : "outline"}
                    onClick={() => setCurrentConduction("bone")}
                  >
                    Bone
                  </Button>
                </div>
              </div>

              {/* Masking */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="masked"
                  checked={isMasked}
                  onChange={(e) => setIsMasked(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="masked" className="cursor-pointer">Masked</Label>
              </div>

              {/* Actions */}
              <div className="pt-4 space-y-2">
                <Button variant="outline" className="w-full" onClick={removeLastPoint}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Undo Last Point
                </Button>
                <Button variant="outline" className="w-full text-red-600" onClick={clearAudiogram}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
              </div>

              {/* Notes */}
              <div>
                <Label>Clinical Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any relevant clinical observations..."
                  className="mt-2"
                  rows={3}
                />
              </div>

              {/* Legend */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium mb-2">Legend</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-red-500"></div>
                    <span>Right Ear (Air)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <span className="text-blue-500 font-bold">×</span>
                    </div>
                    <span>Left Ear (Air)</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audiogram Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ear className="w-5 h-5 text-sky-600" />
                Audiogram
              </CardTitle>
              <CardDescription>
                Click on the chart to add threshold points for the selected ear
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="audiogram-canvas cursor-crosshair"
                onClick={handleCanvasClick}
                style={{ height: "500px" }}
                data-testid="audiogram-chart"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="frequency" 
                      scale="log"
                      domain={[125, 8000]}
                      ticks={FREQUENCIES}
                      tickFormatter={(v) => v >= 1000 ? `${v/1000}k` : v}
                      label={{ value: "Frequency (Hz)", position: "bottom", offset: 40 }}
                      stroke="#94a3b8"
                    />
                    <YAxis 
                      reversed={true}
                      domain={[-10, 120]}
                      ticks={DB_LEVELS}
                      label={{ value: "Hearing Level (dB HL)", angle: -90, position: "insideLeft", offset: 10 }}
                      stroke="#94a3b8"
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white p-2 shadow-lg rounded border text-sm">
                              <p className="font-medium">{label} Hz</p>
                              {payload.map((p, i) => (
                                <p key={i} style={{ color: p.color }}>
                                  {p.name}: {p.value} dB
                                </p>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: 20 }}
                    />
                    
                    {/* Reference lines for severity zones */}
                    <ReferenceLine y={25} stroke="#10b981" strokeDasharray="5 5" label={{ value: "Normal", fill: "#10b981", fontSize: 10 }} />
                    <ReferenceLine y={40} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: "Mild", fill: "#f59e0b", fontSize: 10 }} />
                    <ReferenceLine y={55} stroke="#f97316" strokeDasharray="5 5" label={{ value: "Moderate", fill: "#f97316", fontSize: 10 }} />
                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "Severe", fill: "#ef4444", fontSize: 10 }} />
                    
                    {/* Right Ear Air Conduction */}
                    <Line
                      type="linear"
                      dataKey="rightAir"
                      name="Right Air"
                      stroke="#EF4444"
                      strokeWidth={2}
                      dot={{ fill: "none", stroke: "#EF4444", strokeWidth: 2, r: 6 }}
                      connectNulls={false}
                    />
                    
                    {/* Left Ear Air Conduction */}
                    <Line
                      type="linear"
                      dataKey="leftAir"
                      name="Left Air"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ fill: "#3B82F6", stroke: "#3B82F6", strokeWidth: 2, r: 4 }}
                      connectNulls={false}
                    />
                    
                    {/* Right Ear Bone Conduction */}
                    <Line
                      type="linear"
                      dataKey="rightBone"
                      name="Right Bone"
                      stroke="#EF4444"
                      strokeWidth={1}
                      strokeDasharray="5 5"
                      dot={{ fill: "none", stroke: "#EF4444", strokeWidth: 2, r: 4 }}
                      connectNulls={false}
                    />
                    
                    {/* Left Ear Bone Conduction */}
                    <Line
                      type="linear"
                      dataKey="leftBone"
                      name="Left Bone"
                      stroke="#3B82F6"
                      strokeWidth={1}
                      strokeDasharray="5 5"
                      dot={{ fill: "none", stroke: "#3B82F6", strokeWidth: 2, r: 4 }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Data Summary */}
              {audiogramData.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="font-medium text-red-800">Right Ear</p>
                    <p className="text-sm text-red-600 mt-1">
                      {rightAirData.length} air points, {rightBoneData.length} bone points
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-800">Left Ear</p>
                    <p className="text-sm text-blue-600 mt-1">
                      {leftAirData.length} air points, {leftBoneData.length} bone points
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Recommendations Dialog */}
      <Dialog open={showRecommendations} onOpenChange={setShowRecommendations}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              AI Hearing Aid Recommendations
            </DialogTitle>
            <DialogDescription>
              Based on the PTA test results, here are the recommended hearing aids
            </DialogDescription>
          </DialogHeader>

          {loadingRecommendations ? (
            <div className="py-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-500">Analyzing audiogram and generating recommendations...</p>
            </div>
          ) : aiRecommendations ? (
            <div className="space-y-6 mt-4">
              {/* Severity Assessment */}
              <div className="bg-gradient-to-r from-sky-50 to-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-800 mb-3">Hearing Assessment Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-slate-500">Right Ear</p>
                    <p className="font-bold text-lg">
                      {aiRecommendations.severity_assessment?.right?.pta?.toFixed(1) || "N/A"} dB
                    </p>
                    <Badge className="mt-1 bg-red-100 text-red-700">
                      {aiRecommendations.severity_assessment?.right?.severity || "Pending"}
                    </Badge>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-slate-500">Left Ear</p>
                    <p className="font-bold text-lg">
                      {aiRecommendations.severity_assessment?.left?.pta?.toFixed(1) || "N/A"} dB
                    </p>
                    <Badge className="mt-1 bg-blue-100 text-blue-700">
                      {aiRecommendations.severity_assessment?.left?.severity || "Pending"}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Recommended Devices */}
              {aiRecommendations.quick_recommendations?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    Top Recommended Hearing Aids
                  </h3>
                  <div className="space-y-3">
                    {aiRecommendations.quick_recommendations.map((aid, index) => (
                      <div 
                        key={aid.id || index} 
                        className={`flex items-center gap-4 p-4 rounded-lg border ${index === 0 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">{aid.name}</p>
                          <p className="text-sm text-slate-500">{aid.form_factor} • {aid.match_reason}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">₹{aid.price?.toLocaleString()}</p>
                          {index === 0 && (
                            <Badge className="bg-amber-100 text-amber-700">Best Match</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Counseling Note */}
              {aiRecommendations.counseling_note && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Counseling Note:</strong> {aiRecommendations.counseling_note}
                  </p>
                </div>
              )}

              {/* Next Steps */}
              {aiRecommendations.next_steps && (
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-800">
                    <strong>Next Steps:</strong> {aiRecommendations.next_steps}
                  </p>
                </div>
              )}
            </div>
          ) : null}

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={handleContinue}>
              View Patient Profile
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleGoToRecommendations}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Full AI Recommendations
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
